import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, LogOut, Plus, AlertCircle, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import AdminLogin from './AdminLogin';
import { ProductRow } from '../components/admin/ProductRow';
import {
  isLoggedIn, clearPin,
  listProducts, updateProduct, deleteProduct, createProduct,
} from '../lib/adminApi';

const EMPTY_NEW = { name: '', price: '', category: '', image_url: '' };

const NewProductForm = ({ onCreate, busy }) => {
  const [draft, setDraft] = useState(EMPTY_NEW);
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.name || !draft.price) return;
    await onCreate({
      name: draft.name,
      price: parseFloat(draft.price),
      category: draft.category,
      image_url: draft.image_url || null,
      is_visible: true,
      in_stock: true,
    });
    setDraft(EMPTY_NEW);
    setOpen(false);
  };

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        data-testid="admin-new-product-open"
        className="bg-primary hover:bg-primary/90 text-primary-foreground"
      >
        <Plus className="w-4 h-4 mr-1" /> Nieuw product
      </Button>
    );
  }

  return (
    <Card className="p-5 bg-card border-2 border-primary/40 mb-4">
      <h3 className="text-lg font-bold text-foreground mb-4">Nieuw product</h3>
      <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
        <Input placeholder="Naam" value={draft.name}
          onChange={e => setDraft({ ...draft, name: e.target.value })} data-testid="admin-new-name" />
        <Input placeholder="Prijs (€)" type="number" step="0.01" min="0" value={draft.price}
          onChange={e => setDraft({ ...draft, price: e.target.value })} data-testid="admin-new-price" />
        <Input placeholder="Categorie (bv. burger)" value={draft.category}
          onChange={e => setDraft({ ...draft, category: e.target.value })} data-testid="admin-new-category" />
        <Input placeholder="Afbeelding URL" value={draft.image_url}
          onChange={e => setDraft({ ...draft, image_url: e.target.value })} data-testid="admin-new-image" />
        <div className="sm:col-span-2 flex gap-2 justify-end mt-2">
          <Button type="button" variant="outline" onClick={() => { setOpen(false); setDraft(EMPTY_NEW); }}>
            Annuleer
          </Button>
          <Button type="submit" disabled={busy || !draft.name || !draft.price}
            data-testid="admin-new-submit"
            className="bg-primary hover:bg-primary/90 text-primary-foreground">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aanmaken'}
          </Button>
        </div>
      </form>
    </Card>
  );
};

const ProductList = ({ products, savingId, deletingId, onUpdate, onDelete }) => {
  if (products.length === 0) {
    return <p className="text-muted-foreground text-center py-12">Nog geen producten</p>;
  }
  return (
    <div className="space-y-3">
      {products.map(p => (
        <ProductRow
          key={p.id}
          product={p}
          onUpdate={onUpdate}
          onDelete={onDelete}
          saving={savingId === p.id}
          deleting={deletingId === p.id}
        />
      ))}
    </div>
  );
};

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(isLoggedIn());
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [creating, setCreating] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listProducts();
      setProducts(data);
      setError(null);
    } catch (err) {
      if (err.message === 'Niet ingelogd') {
        setAuthed(false);
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed) refresh();
  }, [authed, refresh]);

  const handleUpdate = async (id, patch) => {
    setSavingId(id);
    setError(null);
    try {
      const updated = await updateProduct(id, patch);
      setProducts(prev => prev.map(p => p.id === id ? { ...p, ...updated } : p));
    } catch (err) {
      setError(err.message);
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`"${name}" verwijderen?`)) return;
    setDeletingId(id);
    setError(null);
    try {
      await deleteProduct(id);
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleCreate = async (payload) => {
    setCreating(true);
    setError(null);
    try {
      const row = await createProduct(payload);
      setProducts(prev => [...prev, row].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = () => {
    clearPin();
    setAuthed(false);
  };

  if (!authed) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-background pb-12">
      <header className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b border-border px-6 py-4 flex items-center gap-3">
        <ShoppingBag className="w-7 h-7 text-primary" />
        <h1 className="text-2xl font-bold text-foreground">Admin · Producten</h1>
        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="outline" size="sm"
            onClick={() => navigate('/kitchen')}
            data-testid="admin-kitchen-link"
          >
            Kitchen Display
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={handleLogout}
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-1" /> Uitloggen
          </Button>
        </div>
      </header>

      <div className="container max-w-5xl mx-auto px-6 py-6">
        {error && (
          <div className="mb-4 bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-2 rounded flex items-center gap-2 text-sm">
            <AlertCircle className="w-4 h-4" /> {error}
          </div>
        )}

        <div className="flex justify-between items-center mb-4">
          <p className="text-sm text-muted-foreground">{products.length} producten</p>
          <NewProductForm onCreate={handleCreate} busy={creating} />
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-10 h-10 text-primary animate-spin" /></div>
        ) : (
          <ProductList
            products={products}
            savingId={savingId}
            deletingId={deletingId}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
